import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast, showSuccessToast } from "#app/notifications";
import {
  getQuoteRequestLineQueryData,
  setQuoteRequestLineQueryData,
} from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { validateCosting } from "#modules/quoteRequestLines/infra/validateCosting.api";
import { useAnalytics, AnalyticsEvent } from "#shared/useAnalytics";

export const useValidateCosting = (
  quoteRequestId: string | undefined,
  options?: {
    onSuccess?: () => void;
    onError?: (err: Error) => void;
    onMutate?: () => void;
  },
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { mutate, ...rest } = useMutation({
    mutationFn: ({ quoteRequestLineIds }: { quoteRequestLineIds: string[] }) =>
      validateCosting({ quoteRequestLineIds }),
    onMutate: () => {
      options?.onMutate?.();
    },
    onSuccess: (quoteRequestLines, variables) => {
      quoteRequestLines.validatedQuoteRequestLinesIds.forEach((id) => {
        const existingData = getQuoteRequestLineQueryData(queryClient, id);
        if (existingData) {
          setQuoteRequestLineQueryData(queryClient, id, {
            ...existingData,
            isReliable: true,
          });
        }
      });
      quoteRequestLines.unvalidatedQuoteRequestLinesIds.forEach((id) => {
        const existingData = getQuoteRequestLineQueryData(queryClient, id);
        if (existingData) {
          setQuoteRequestLineQueryData(queryClient, id, {
            ...existingData,
            isReliable: false,
          });
        }
      });

      const isSingleValidation = variables.quoteRequestLineIds.length === 1;
      const validatedCount = quoteRequestLines.validatedQuoteRequestLinesIds.length;
      const atLeastOneReliable = validatedCount > 0;

      analytics.capture(AnalyticsEvent.PredictionValidated, {
        count: validatedCount,
        is_batch: !isSingleValidation,
        validated: atLeastOneReliable,
        quote_request_line_ids: quoteRequestLines.validatedQuoteRequestLinesIds,
        quote_request_id: quoteRequestId,
      });

      if (isSingleValidation) {
        const toastTitle = atLeastOneReliable
          ? t("quoteRequestLine.validated")
          : t("quoteRequestLine.unvalidated");

        showSuccessToast({
          title: toastTitle,
        });
      } else {
        const toastTitle = t("quoteRequestLine.bulkValidated", {
          count: validatedCount,
          total: variables.quoteRequestLineIds.length,
        });

        showSuccessToast({
          title: toastTitle,
        });
      }

      options?.onSuccess?.();
    },
    onError: (err: Error) => {
      showErrorToast({ title: err.message });
      errorHandler(err);

      options?.onError?.(err);
    },
  });

  const validateCostingSingle = (quoteRequestLineId: string) => {
    mutate({ quoteRequestLineIds: [quoteRequestLineId] });
  };

  const validateCostingBulk = (quoteRequestLineIds: string[]) => {
    mutate({ quoteRequestLineIds });
  };

  return {
    validateCosting: validateCostingSingle,
    validateCostingBulk,
    ...rest,
  };
};
